import {createSlice, UnknownAction} from '@reduxjs/toolkit';
import {
  ApiState,
  createInitialApiState,
  setApiPending,
  setApiRejected,
  setApiSuccess,
} from '../apiState';
import {apiErrorMessage, isApiError} from '../../lib/apiError';
import {
  AdminConversion,
  AdminDashboardStats,
  AdminEmail,
  AdminPage,
  AdminSignup,
  AdminSuppression,
  AdminTranscription,
  AdminUser,
  AdminUserProfile,
} from '../../domain/admin';
import {AdminWordlist, AdminWordlistDetail} from '../../domain/adminWordlists';
import {
  NOT_ADMIN_CODE,
  fetchAdminEmails,
  fetchAdminSignups,
  fetchAdminStats,
  fetchAdminSuppressions,
  fetchAdminTranscriptionQueue,
  fetchAdminUserConversions,
  fetchAdminUserDetail,
  fetchAdminUserEmails,
  fetchAdminUserTranscriptions,
  fetchAdminUsers,
  resendSetupEmail,
} from './adminThunks';
import {
  createAdminWordlist,
  deleteAdminWordlist,
  fetchAdminWordlistDetail,
  fetchAdminWordlists,
  updateAdminWordlist,
} from './adminWordlistsThunks';

interface AdminState {
  api: {
    stats: ApiState;
    users: ApiState;
    userDetail: ApiState;
    userTranscriptions: ApiState;
    userConversions: ApiState;
    userEmails: ApiState;
    resendSetupEmail: ApiState;
    signups: ApiState;
    emails: ApiState;
    suppressions: ApiState;
    transcriptionQueue: ApiState;
    wordlists: ApiState;
    wordlistDetail: ApiState;
    wordlistCreate: ApiState;
    wordlistSave: ApiState;
    wordlistDelete: ApiState;
  };
  data: {
    stats: AdminDashboardStats | null;
    users: AdminPage<AdminUser> | null;
    /** The detail-state twin of `users`, per the slice convention: list and selection together. */
    userDetail: AdminUserProfile | null;
    userTranscriptions: AdminPage<AdminTranscription> | null;
    userConversions: AdminPage<AdminConversion> | null;
    userEmails: AdminPage<AdminEmail> | null;
    signups: AdminPage<AdminSignup> | null;
    /** The global email log — the work queue behind `emailsUndelivered30d`. */
    emails: AdminPage<AdminEmail> | null;
    suppressions: AdminPage<AdminSuppression> | null;
    /** The global transcription queue, for finding a stuck job regardless of owner. */
    transcriptionQueue: AdminPage<AdminTranscription> | null;
    wordlists: AdminPage<AdminWordlist> | null;
    /** The detail-state twin of `wordlists`, per the slice convention. */
    wordlistDetail: AdminWordlistDetail | null;
  };
  /**
   * Set the first time any admin request comes back 404 — which is how the API says "not an
   * admin", never "no such thing". One flag for the whole section, so `AdminRoute` can bounce
   * rather than each screen having to interpret its own 404.
   *
   * Deliberately not seeded from the JWT claim: the claim is stamped at token issue and survives a
   * revoke, so it can open the door but must never be what closes it.
   */
  accessDenied: boolean;
}

const initialState: AdminState = {
  api: {
    stats: createInitialApiState(),
    users: createInitialApiState(),
    userDetail: createInitialApiState(),
    userTranscriptions: createInitialApiState(),
    userConversions: createInitialApiState(),
    userEmails: createInitialApiState(),
    resendSetupEmail: createInitialApiState(),
    signups: createInitialApiState(),
    emails: createInitialApiState(),
    suppressions: createInitialApiState(),
    transcriptionQueue: createInitialApiState(),
    wordlists: createInitialApiState(),
    wordlistDetail: createInitialApiState(),
    wordlistCreate: createInitialApiState(),
    wordlistSave: createInitialApiState(),
    wordlistDelete: createInitialApiState(),
  },
  data: {
    stats: null,
    users: null,
    userDetail: null,
    userTranscriptions: null,
    userConversions: null,
    userEmails: null,
    signups: null,
    emails: null,
    suppressions: null,
    transcriptionQueue: null,
    wordlists: null,
    wordlistDetail: null,
  },
  accessDenied: false,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    /**
     * Wipes the selected user and everything hanging off them. The detail screen dispatches this
     * when the id in the URL changes: without it, the previous user's transcriptions sit under the
     * new user's name for as long as the refetch takes, which on an admin screen is the kind of
     * wrong that gets acted on.
     */
    clearAdminUserDetail: (state) => {
      state.data.userDetail = null;
      state.data.userTranscriptions = null;
      state.data.userConversions = null;
      state.data.userEmails = null;
      state.api.userDetail = createInitialApiState();
      state.api.userTranscriptions = createInitialApiState();
      state.api.userConversions = createInitialApiState();
      state.api.userEmails = createInitialApiState();
      state.api.resendSetupEmail = createInitialApiState();
    },
    /**
     * Wipes the selected word list. The detail screen dispatches this when the id in the URL
     * changes, for the same reason `clearAdminUserDetail` does — otherwise the previous list's
     * words sit under the new id's name for as long as the refetch takes.
     */
    clearAdminWordlistDetail: (state) => {
      state.data.wordlistDetail = null;
      state.api.wordlistDetail = createInitialApiState();
      state.api.wordlistSave = createInitialApiState();
      state.api.wordlistDelete = createInitialApiState();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminStats.pending, (state) => {
        setApiPending(state.api.stats);
      })
      .addCase(fetchAdminStats.fulfilled, (state, action) => {
        setApiSuccess(state.api.stats);
        state.data.stats = action.payload;
      })
      .addCase(fetchAdminStats.rejected, (state, action) => {
        setApiRejected(state.api.stats, apiErrorMessage(action.payload, 'Could not load the admin dashboard'));
      });

    builder
      .addCase(fetchAdminUsers.pending, (state) => {
        setApiPending(state.api.users);
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        setApiSuccess(state.api.users);
        state.data.users = action.payload;
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        setApiRejected(state.api.users, apiErrorMessage(action.payload, 'Could not load users'));
      });

    builder
      .addCase(fetchAdminUserDetail.pending, (state) => {
        setApiPending(state.api.userDetail);
      })
      .addCase(fetchAdminUserDetail.fulfilled, (state, action) => {
        setApiSuccess(state.api.userDetail);
        state.data.userDetail = action.payload;
      })
      .addCase(fetchAdminUserDetail.rejected, (state, action) => {
        setApiRejected(state.api.userDetail, apiErrorMessage(action.payload, 'Could not load this user'));
      });

    builder
      .addCase(fetchAdminUserTranscriptions.pending, (state) => {
        setApiPending(state.api.userTranscriptions);
      })
      .addCase(fetchAdminUserTranscriptions.fulfilled, (state, action) => {
        setApiSuccess(state.api.userTranscriptions);
        state.data.userTranscriptions = action.payload;
      })
      .addCase(fetchAdminUserTranscriptions.rejected, (state, action) => {
        setApiRejected(
          state.api.userTranscriptions,
          apiErrorMessage(action.payload, 'Could not load transcriptions')
        );
      });

    builder
      .addCase(fetchAdminUserConversions.pending, (state) => {
        setApiPending(state.api.userConversions);
      })
      .addCase(fetchAdminUserConversions.fulfilled, (state, action) => {
        setApiSuccess(state.api.userConversions);
        state.data.userConversions = action.payload;
      })
      .addCase(fetchAdminUserConversions.rejected, (state, action) => {
        setApiRejected(
          state.api.userConversions,
          apiErrorMessage(action.payload, 'Could not load conversions')
        );
      });

    builder
      .addCase(fetchAdminUserEmails.pending, (state) => {
        setApiPending(state.api.userEmails);
      })
      .addCase(fetchAdminUserEmails.fulfilled, (state, action) => {
        setApiSuccess(state.api.userEmails);
        state.data.userEmails = action.payload;
      })
      .addCase(fetchAdminUserEmails.rejected, (state, action) => {
        setApiRejected(
          state.api.userEmails,
          apiErrorMessage(action.payload, 'Could not load the email history')
        );
      });

    builder
      .addCase(resendSetupEmail.pending, (state) => {
        setApiPending(state.api.resendSetupEmail);
      })
      .addCase(resendSetupEmail.fulfilled, (state) => {
        setApiSuccess(state.api.resendSetupEmail);
      })
      .addCase(resendSetupEmail.rejected, (state, action) => {
        setApiRejected(
          state.api.resendSetupEmail,
          apiErrorMessage(action.payload, 'The setup email could not be sent')
        );
      });

    builder
      .addCase(fetchAdminSignups.pending, (state) => {
        setApiPending(state.api.signups);
      })
      .addCase(fetchAdminSignups.fulfilled, (state, action) => {
        setApiSuccess(state.api.signups);
        state.data.signups = action.payload;
      })
      .addCase(fetchAdminSignups.rejected, (state, action) => {
        setApiRejected(state.api.signups, apiErrorMessage(action.payload, 'Could not load the signup queue'));
      });

    builder
      .addCase(fetchAdminEmails.pending, (state) => {
        setApiPending(state.api.emails);
      })
      .addCase(fetchAdminEmails.fulfilled, (state, action) => {
        setApiSuccess(state.api.emails);
        state.data.emails = action.payload;
      })
      .addCase(fetchAdminEmails.rejected, (state, action) => {
        setApiRejected(state.api.emails, apiErrorMessage(action.payload, 'Could not load the email log'));
      });

    builder
      .addCase(fetchAdminSuppressions.pending, (state) => {
        setApiPending(state.api.suppressions);
      })
      .addCase(fetchAdminSuppressions.fulfilled, (state, action) => {
        setApiSuccess(state.api.suppressions);
        state.data.suppressions = action.payload;
      })
      .addCase(fetchAdminSuppressions.rejected, (state, action) => {
        setApiRejected(
          state.api.suppressions,
          apiErrorMessage(action.payload, 'Could not load the suppression list')
        );
      });

    builder
      .addCase(fetchAdminTranscriptionQueue.pending, (state) => {
        setApiPending(state.api.transcriptionQueue);
      })
      .addCase(fetchAdminTranscriptionQueue.fulfilled, (state, action) => {
        setApiSuccess(state.api.transcriptionQueue);
        state.data.transcriptionQueue = action.payload;
      })
      .addCase(fetchAdminTranscriptionQueue.rejected, (state, action) => {
        setApiRejected(
          state.api.transcriptionQueue,
          apiErrorMessage(action.payload, 'Could not load the transcription queue')
        );
      });

    builder
      .addCase(fetchAdminWordlists.pending, (state) => {
        setApiPending(state.api.wordlists);
      })
      .addCase(fetchAdminWordlists.fulfilled, (state, action) => {
        setApiSuccess(state.api.wordlists);
        state.data.wordlists = action.payload;
      })
      .addCase(fetchAdminWordlists.rejected, (state, action) => {
        setApiRejected(state.api.wordlists, apiErrorMessage(action.payload, 'Could not load word lists'));
      });

    builder
      .addCase(fetchAdminWordlistDetail.pending, (state) => {
        setApiPending(state.api.wordlistDetail);
      })
      .addCase(fetchAdminWordlistDetail.fulfilled, (state, action) => {
        setApiSuccess(state.api.wordlistDetail);
        state.data.wordlistDetail = action.payload;
      })
      .addCase(fetchAdminWordlistDetail.rejected, (state, action) => {
        setApiRejected(
          state.api.wordlistDetail,
          apiErrorMessage(action.payload, 'Could not load this word list')
        );
      });

    // Create/update/delete only track their own request state — the create modal navigates to the
    // new id on success, and the edit/delete/restore actions on the detail page refetch it, the
    // same way `resendSetupEmail` refetches the user profile rather than hand-patching it in here.
    builder
      .addCase(createAdminWordlist.pending, (state) => {
        setApiPending(state.api.wordlistCreate);
      })
      .addCase(createAdminWordlist.fulfilled, (state) => {
        setApiSuccess(state.api.wordlistCreate);
      })
      .addCase(createAdminWordlist.rejected, (state, action) => {
        setApiRejected(
          state.api.wordlistCreate,
          apiErrorMessage(action.payload, 'Could not create the word list')
        );
      });

    builder
      .addCase(updateAdminWordlist.pending, (state) => {
        setApiPending(state.api.wordlistSave);
      })
      .addCase(updateAdminWordlist.fulfilled, (state) => {
        setApiSuccess(state.api.wordlistSave);
      })
      .addCase(updateAdminWordlist.rejected, (state, action) => {
        setApiRejected(
          state.api.wordlistSave,
          apiErrorMessage(action.payload, 'Could not update this word list')
        );
      });

    builder
      .addCase(deleteAdminWordlist.pending, (state) => {
        setApiPending(state.api.wordlistDelete);
      })
      .addCase(deleteAdminWordlist.fulfilled, (state) => {
        setApiSuccess(state.api.wordlistDelete);
      })
      .addCase(deleteAdminWordlist.rejected, (state, action) => {
        setApiRejected(
          state.api.wordlistDelete,
          apiErrorMessage(action.payload, 'Could not delete this word list')
        );
      });

    /**
     * One matcher instead of eleven copies of the same `if`: any admin request that came back 404
     * has told us this account is not an admin, whichever screen happened to ask.
     */
    builder.addMatcher(
      (action: UnknownAction) =>
        action.type.startsWith('admin/') && action.type.endsWith('/rejected'),
      (state, action: UnknownAction & {payload?: unknown}) => {
        if (isApiError(action.payload) && action.payload.code === NOT_ADMIN_CODE) {
          state.accessDenied = true;
        }
      }
    );
  },
});

export const {clearAdminUserDetail, clearAdminWordlistDetail} = adminSlice.actions;
export default adminSlice.reducer;
